<template>
    <v-container fluid class="white px-0 elevation-4 pt-0">
        <main-app-bar title="Clinic Order Templates">
            <template v-slot:buttons>
                <v-btn class="mx-4" @click="$router.push({ name: 'stockOrders' })">Cancel</v-btn>
                <v-btn color="primary" :loading="pageLoading" @click="submitForm" :disabled="!isOrderTemplateCreated || pageLoading">Save</v-btn>
            </template>
        </main-app-bar>
        <v-flex class="d-block grey lighten-4">
            <div class="pa-3">
                <v-btn-toggle
                    v-model="orderType"
                    color="primary"
                    tile
                    group
                    mandatory
                >
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="Stock">
                        Stock
                    </v-btn>
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="Stationary">
                        Stationary
                    </v-btn>
                </v-btn-toggle>
            </div>
        </v-flex>
        <v-form ref="mainForm" v-if="!pageLoading && isOrderTemplateCreated && !noMasterError">
            <v-alert v-show="error" type="error" style="border-radius: 0" class="pa-3">
                {{error}}
            </v-alert>
            <div class="mb-5 pt-5 px-4 d-flex align-center" style="max-width: 500px; position: relative;">
                <h4 style="width: 100%;">Monthly Budget, £ (inc. VAT)</h4>
                <v-text-field style="width: 100%;" class="ml-5" v-model="monthlyBudget" :rules="monthlyBudgetRules" label="Monthly budget" solo dense
                              ></v-text-field>
            </div>
            <v-simple-table class="order-template-table">
                <template v-slot:default>
                    <thead>
                    <tr>
                        <th class="text-center">Product</th>
                        <th class="text-center">Supplier</th>
                        <th class="text-center">Code</th>
                        <th class="text-center">Base</th>
                        <th class="text-center">Price, &#163; (inc. VAT)</th>
                        <th class="text-left">Exclude</th>
                    </tr>
                    </thead>
                    <tbody>
                    <template v-for="(sortedGroup, index) in sortedGroups" >
                        <tr :key="'group-' + index">
                            <td colspan="6" style="text-align: left"><h4>List {{ sortedGroup.sortedNumber }}</h4></td>
                        </tr>
                        <template v-for="(sortedLetter, letterIndex) in sortedGroup.sortedLetters">
                            <tr :key="'group-letter-' + index + letterIndex">
                                <td colspan="6" style="text-align: left; padding-left: 70px;"><h4>{{ sortedLetter }}</h4></td>
                            </tr>
                            <tr v-for="(product) in baseProducts" :key="'products-' + product.id + letterIndex + index">
                                <template v-if="product.groupNumber === sortedGroup.sortedNumber && product.groupLetter === sortedLetter">
                                    <td class="py-2">
                                        <v-text-field disabled v-model="product.title" solo dense class="elevation-0"></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field disabled v-model="product.supplier" solo dense class="elevation-0"></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field disabled v-model="product.code" solo dense class="elevation-0"></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field type="number" min="0" v-model="product.base" solo dense ></v-text-field>
                                    </td>
                                    <td>
                                        <v-text-field disabled v-model="product.price" solo dense></v-text-field>
                                    </td>
                                    <td style="width: 30px;">
                                        <v-checkbox v-model="product.isDisabled" solo dense></v-checkbox>
                                    </td>
                                </template>
                            </tr>
                        </template>
                    </template>
                    </tbody>
                </template>
            </v-simple-table>
        </v-form>
        <v-skeleton-loader v-if="pageLoading" type="table"></v-skeleton-loader>
        <div v-if="!isOrderTemplateCreated && !pageLoading && !noMasterError" class="text-center pt-4 pb-2">
            <h3>There is no order template in current clinic!</h3>
            <v-btn class="mt-4" color="primary" @click="createBtnHandler">create</v-btn>
        </div>
        <div v-else-if="noMasterError" class="text-center pt-4 pb-2">
            <h3>Sorry, there is no template available in this group</h3>
        </div>
    </v-container>
</template>

<script>
    import MainAppBar from '@/components/MainAppBar'
    import { mapActions, mapGetters } from 'vuex'
    import { stockSortingBySublists } from '@/common/functions'

    export default {
        components: {
            MainAppBar
        },
        data: () => ({
            sortedGroups: [],
            baseProducts: [],
            monthlyBudget: null,
            orderTemplateCreating: false,
            isOrderTemplateCreated: false,
            pageLoading: false,
            error: '',
            noMasterError: false,
            orderType: 'Stock'
        }),
        computed: {
            ...mapGetters({
                userClinicId: 'getUserActiveClinic'
            }),
            monthlyBudgetRules () {
                return [
                    val => !!val || 'Monthly budget is required!',
                    val => {
                        if ((typeof val === 'string' && Number(val.trim())) ||
                            (typeof val === 'number') ||
                            (typeof val === 'string' && val.trim().length && Number(val.trim()) === 0)) {
                            return true
                        } else {
                            return 'Must be a number'
                        }
                    }
                ]
            }
        },
        watch: {
            userClinicId () {
                this.getOrderTemplate()
            },
            orderType () {
                this.noMasterError = false
                this.getOrderTemplate()
            }
        },
        methods: {
            ...mapActions(['fetchOrderTemplate', 'updateOrderTemplate']),
            createBtnHandler () {
                this.isOrderTemplateCreated = true
                this.getOrderTemplateByGroup()
            },
            async submitForm () {
                this.error = ''
                this.pageLoading = true
              if (this.checkIsProductsValid(this.orderProducts) && this.checkIsProductsValid(this.baseProducts) && this.$refs.mainForm.validate()) {
                    await this.updateOrderTemplate(this.createData())
                } else {
                    this.error = 'Please fill all fields below!'
                }
                this.pageLoading = false
            },
            checkIsProductsValid (products) {
                let isValid = true

                products.forEach(product => {
                    if (!product.title || !product.supplier || !product.code || !product.price || !product.base) {
                        isValid = false
                    }
                })

                return isValid
            },
            createData () {
                return {
                    orderType: this.orderType,
                    clinicId: this.userClinicId,
                    monthlyBudget: Number(this.monthlyBudget),
                    products: this.orderProducts.map(item => {
                      return {
                        ...item,
                        price: Number(item.price),
                        base: Number(item.base)
                      }
                    }),
                    baseProducts: this.baseProducts.map(item => {
                      return {
                        id: item.baseProductId ? item.baseProductId : item.id,
                        base: Number(item.base),
                        isDisabled: item.isDisabled
                      }
                    })
                }
            },
            sortProducts () {
              this.baseProducts.sort(stockSortingBySublists)
              this.baseProducts.forEach(product => {
                const sortedItem = this.sortedGroups.find(item => product.groupNumber === item.sortedNumber)
                if (!sortedItem) {
                  const sortedValue = {
                    sortedNumber: product.groupNumber,
                    sortedLetters: []
                  }
                  if (!sortedValue.sortedLetters.find(item => product.groupLetter === item)) {
                    sortedValue.sortedLetters.push(product.groupLetter)
                  }
                  this.sortedGroups.push(sortedValue)
                } else {
                  if (!sortedItem.sortedLetters.find(item => product.groupLetter === item)) {
                    sortedItem.sortedLetters.push(product.groupLetter)
                  }
                }
              })
            },
            cleanInitialData () {
              this.monthlyBudget = null
              this.error = ''
              this.orderProducts = []
              this.baseProducts = []
              this.sortedGroups = []
            },
            async getOrderTemplate () {
                this.pageLoading = true
                this.cleanInitialData()
                const params = {
                  orderType: this.orderType,
                  groupId: this.$store.getters.getUserActiveGroup,
                  clinicId: this.userClinicId
                }
                const result = await this.fetchOrderTemplate(params) || {}
                this.monthlyBudget = result.monthlyBudget || ''
                if (result.products) {
                  this.baseProducts = result.products || []
                  this.sortProducts()
                }
                const noBaseProducts = this.baseProducts.filter(item => !item.base)
                if (noBaseProducts.length) {
                  this.error = 'This template has been updated. Please fill all required fields before save'
                }
              this.isOrderTemplateCreated = this.baseProducts.length
                this.pageLoading = false
            },
          async getOrderTemplateByGroup () {
            this.pageLoading = true
            this.cleanInitialData()
            const params = {
              orderType: this.orderType,
              groupId: this.$store.getters.getUserActiveGroup
            }
            const result = await this.fetchOrderTemplate(params) || {}
            this.monthlyBudget = result.monthlyBudget || ''
            this.baseProducts = result.products || []
            if (result.products) {
              this.sortProducts()
            }
            this.noMasterError = !this.baseProducts.length
            const noBaseProducts = this.baseProducts.filter(item => !item.base)
            if (noBaseProducts.length) {
              this.error = 'This template has been updated. Please fill all required fields before save'
            }
            this.isOrderTemplateCreated = !!result.products
            this.pageLoading = false
          }
        },
        async mounted () {
            this.getOrderTemplate()
        }
    }
</script>

<style lang="scss">
    .order-template-table {
        tbody {
            td {
                max-width: 100px;
            }
        }
    }
</style>
